import React, { useState } from 'react';
import { postRecordCommunication } from '../api';
import { CommunicationType, ServiceRecord } from '../types';
import { buildCompletionText, buildIntakeText, buildRepairUpdateText } from '../utils/messageGenerator';

interface ClientMessageSectionProps {
  record: ServiceRecord;
  onCopySuccess: (msg: string) => void;
  onCommunicationAdded?: () => void;
}

type TemplateType = 'INTAKE' | 'UPDATE' | 'COMPLETION';

export const ClientMessageSection: React.FC<ClientMessageSectionProps> = ({
  record,
  onCopySuccess,
  onCommunicationAdded,
}) => {
  const [templateType, setTemplateType] = useState<TemplateType>('INTAKE');
  const [intakeCustomText, setIntakeCustomText] = useState('');
  const [repairCustomText, setRepairCustomText] = useState('');
  const [readyCustomText, setReadyCustomText] = useState('');

  let generatedText = '';
  if (templateType === 'INTAKE') {
    generatedText = buildIntakeText(record, intakeCustomText);
  } else if (templateType === 'UPDATE') {
    generatedText = buildRepairUpdateText(record, repairCustomText);
  } else if (templateType === 'COMPLETION') {
    generatedText = buildCompletionText(record, readyCustomText);
  }

  const handleCopyMessage = async () => {
    try {
      await navigator.clipboard.writeText(generatedText);
      onCopySuccess('Skopiowano treść wiadomości dla klienta do schowka');
    } catch (e) {
      onCopySuccess('Błąd kopiowania do schowka');
    }

    // Niezależny zapis historii komunikacji do Central API (zgodnie z wymaganiem 3 i 16)
    try {
      let commType: CommunicationType = 'RECEIVED_CONFIRMATION';
      if (templateType === 'UPDATE') commType = 'REPAIR_UPDATE';
      if (templateType === 'COMPLETION') commType = 'READY_FOR_PICKUP';

      await postRecordCommunication(record.uuid, {
        type: commType,
        channel: 'CLIPBOARD',
        action: 'COPIED_FOR_SENDING',
        source: 'WEB',
        content: generatedText,
        idempotencyKey: crypto.randomUUID(),
      });

      if (onCommunicationAdded) {
        onCommunicationAdded();
      }
    } catch (e) {
      // Ignorujemy błędy zapisu historii, aby nie blokować kopiowania do schowka
    }
  };

  return (
    <div className="card-container compact-message-card">
      <div className="card-title">
        <span>💬 Aktualizacja dla klienta (SMS / Windows Phone Link)</span>
        <button type="button" className="btn btn-copy" onClick={handleCopyMessage}>
          📋 Kopiuj wiadomość
        </button>
      </div>

      <div className="message-tab-container">
        <button
          type="button"
          className={`status-tab-btn ${templateType === 'INTAKE' ? 'active' : ''}`}
          onClick={() => setTemplateType('INTAKE')}
        >
          Potwierdzenie przyjęcia
        </button>
        <button
          type="button"
          className={`status-tab-btn ${templateType === 'UPDATE' ? 'active' : ''}`}
          onClick={() => setTemplateType('UPDATE')}
        >
          Aktualizacja naprawy
        </button>
        <button
          type="button"
          className={`status-tab-btn ${templateType === 'COMPLETION' ? 'active' : ''}`}
          onClick={() => setTemplateType('COMPLETION')}
        >
          Gotowe do odbioru
        </button>
      </div>

      <div style={{ marginBottom: '10px' }}>
        <label className="field-label" style={{ marginBottom: '4px', display: 'block' }}>
          Własna treść wiadomości (opcjonalna):
        </label>
        {templateType === 'INTAKE' && (
          <textarea
            className="field-input custom-message-textarea"
            placeholder="np. Sprzęt przyjęty bez zasilacza (możesz wpisać lub wkleić dłuższą treść wielowierszową)"
            value={intakeCustomText}
            onChange={(e) => setIntakeCustomText(e.target.value)}
            rows={5}
          />
        )}
        {templateType === 'UPDATE' && (
          <textarea
            className="field-input custom-message-textarea"
            placeholder="np. Oczekujemy na dostawę części (możesz wpisać lub wkleić dłuższą treść wielowierszową)"
            value={repairCustomText}
            onChange={(e) => setRepairCustomText(e.target.value)}
            rows={5}
          />
        )}
        {templateType === 'COMPLETION' && (
          <textarea
            className="field-input custom-message-textarea"
            placeholder="np. Odbiór możliwy dziś do 19:00 (możesz wpisać lub wkleić dłuższą treść wielowierszową)"
            value={readyCustomText}
            onChange={(e) => setReadyCustomText(e.target.value)}
            rows={5}
          />
        )}
      </div>

      <div className="message-preview-box">{generatedText}</div>
    </div>
  );
};
